<template>
  <b-container fluid>
    <h1>Happy Customer Report</h1>

    <section>
      <b-form>
        <b-form-row class="my-2">
          <b-col cols=* class="mt-2">
            <label>Data Range</label>
          </b-col>
          <b-col cols=* class="mt-2">
            <b-form-input type="date" v-model="startDate" @change=clearData />
          </b-col>
          <b-col cols=* class="mt-2">
            <b-form-input type="date" v-model="endDate" @change=clearData />
          </b-col>
          <b-col>
            <b-button size="sm" variant="link" v-b-popover.hover="'These \'summary\' tables do NOT include am-3000a_refurb and am-3000_refurb, per request.'" class='mr-2'>
              <i class="fas fa-question-circle"></i>
            </b-button>
            <b-table :fields="countFields" :items=[{}] :busy=busy>
              <template slot='orders'>
                {{betterRound(totals.hc / totals.order * 100)}}%
                ({{totals.hc | formatNumber}} / {{totals.order | formatNumber}} orders placed)
              </template>
              <template slot='SKU Counts'>
                {{betterRound(totals.hcskus / totals.orderskus * 100)}}%
                ({{totals.hcskus | formatNumber}} / {{totals.orderskus | formatNumber}} order items)
              </template>
              <template slot='itemQuantities'>
                {{betterRound(totals.hcqty / totals.orderqty * 100)}}%
                ({{totals.hcqty | formatNumber}} / {{totals.orderqty | formatNumber}} items shipped)
              </template>
            </b-table>
          </b-col>
        </b-form-row>
        <b-row>
          <b-col>
            <h5><center>Quality Issues</center></h5>
            <b-table :fields=tables.columns :items=tables.quality :busy=busy>
              <template slot=problemProduct slot-scope=row>
                <router-link target="_blank" :to="{name: 'Qty Calculations', query: {partNum: row.item.problemProduct}}">
                  {{row.item.problemProduct}}
                </router-link>
              </template>
              <template slot=orders slot-scope=row>
                <b-btn size="sm" variant="link" class="px-0" @click="row.toggleDetails">
                  <i class="fas fa-arrow-down"></i>
                </b-btn>
              </template>
              <template slot="row-details" slot-scope="row">
                <div v-html=getOrdersHtml(row.item.orders) />
              </template>
            </b-table>
          </b-col>
          <b-col>
            <h5><center>Wrong Part Issues</center></h5>
            <b-table :fields=tables.columns :items=tables.wrongPart :busy=busy>
              <template slot=problemProduct slot-scope=row>
                <router-link target="_blank" :to="{name: 'Qty Calculations', query: {partNum: row.item.problemProduct}}">
                  {{row.item.problemProduct}}
                </router-link>
              </template>
              <template slot=orders slot-scope=row>
                <b-btn size="sm" variant="link" class="px-0" @click="row.toggleDetails">
                  <i class="fas fa-arrow-down"></i>
                </b-btn>
              </template>
              <template slot="row-details" slot-scope="row">
                <div v-html=getOrdersHtml(row.item.orders) />
              </template>
            </b-table>
          </b-col>
          <b-col>
            <h5><center>Missing Issues</center></h5>
            <b-table :fields=tables.columns :items=tables.missing :busy=busy>
              <template slot=problemProduct slot-scope=row>
                <router-link target="_blank" :to="{name: 'Qty Calculations', query: {partNum: row.item.problemProduct}}">
                  {{row.item.problemProduct}}
                </router-link>
              </template>
              <template slot=orders slot-scope=row>
                <b-btn size="sm" variant="link" class="px-0" @click="row.toggleDetails">
                  <i class="fas fa-arrow-down"></i>
                </b-btn>
              </template>
              <template slot="row-details" slot-scope="row">
                <div v-html=getOrdersHtml(row.item.orders) />
              </template>
            </b-table>
          </b-col>
        </b-row>
        <hr class='mt-0' />
        <b-form-row class='mb-1'>
          <b-col>
            <b-form-input v-model="search" placeholder="Search Text (supports wildcard: %)" class='my-2' @keypress.enter=getData />
          </b-col>
          <b-col cols=*>
            <b-form-radio-group v-model="searchOption" :options="searchOptions" stacked size="sm" class='mx-4' />
          </b-col>
          <b-col cols=*>
            <b-button size="sm" variant="link" v-b-modal.search-help class='mr-2'>
              <i class="fas fa-question-circle"></i>
            </b-button>
          </b-col>
          <b-col cols=*>
            <b-btn type="submit" variant=primary @click="getData" class='my-2'>
              Refresh Data
            </b-btn>
          </b-col>
        </b-form-row>
      </b-form>

      <b-row class="my-2" align-h="between">
        <b-col />
        <b-col cols="*">
          <b-table :fields="countFields" :items=[{}] :busy=busy>
              <template slot='orders'>
                {{betterRound(search_totals.hc / search_totals.order * 100)}}%
                ({{search_totals.hc | formatNumber}} / {{search_totals.order | formatNumber}} orders placed)
              </template>
              <template slot='SKU Counts'>
                {{betterRound(search_totals.hcskus / search_totals.orderskus * 100)}}%
                ({{search_totals.hcskus | formatNumber}} / {{search_totals.orderskus | formatNumber}} order items)
              </template>
              <template slot='itemQuantities'>
                {{betterRound(search_totals.hcqty / search_totals.orderqty * 100)}}%
                ({{search_totals.hcqty | formatNumber}} / {{search_totals.orderqty | formatNumber}} items shipped)
              </template>
            </b-table>
        </b-col>
        <b-col />
      </b-row>

      <b-row>
        <b-col class="mx-2" style="margin:auto">
          <h3><center><span>{{ formatDate(startDate) }} - {{ formatDate(endDate) }}</span></center></h3>
        </b-col>
      </b-row>
      <b-row class="my-2" align-h=between>
        <b-col />
        <b-col cols=* class="ml-3 mt-1">
          <label>Counter Type (Year Data Only)</label>
        </b-col>
        <b-col cols=8>
          <b-form-select v-model="selectedCountType" :options="countTypes" />
        </b-col>
        <b-col cols=*>
          "Unknown" Happy Customers?
          <b-button size="sm" variant="link" v-b-modal.unknown-help class='mr-2'>
            <i class="fas fa-question-circle"></i>
          </b-button>
        </b-col>
        <b-col />
      </b-row>
      <b-row class="my-2">
        <b-col cols=1 />
        <b-col>
          <b-table :fields=yearFields :items=yearData :sort-by.sync="sortBy" :sort-desc.sync="sortDesc" :busy=busy>
            <template slot="month" slot-scope="row">
              {{getMonthName(row.item.month, row.item)}}
            </template>
            <template slot="year" slot-scope="row">
              {{row.item.year}}
            </template>
            <template slot="total" slot-scope="row">
              {{formatNumber(row.item.total, row.item)}}
            </template>
            <template v-for="type in hcTypes" :slot="type.key" slot-scope="row">
              {{formatNumber(row.item[type.key], row.item)}}
            </template>
          </b-table>
        </b-col>
        <b-col cols=1 />
      </b-row>
    </section>
    <section>
      <b-row>
        <b-col class='mx-2' style="margin:auto">
          <h3><center><span v-html=monthToDateText /></center></h3>
        </b-col>
      </b-row>
      <b-row>
        <b-col cols=1 />
        <b-col>
          <b-table :fields=monthFields :items="monthData" :busy=busy>
            <template slot="counter" slot-scope="row">
              <strong v-if="selectedCountType == row.item.counter">
                {{countTypes.find(type => type.value == row.item.counter).text}}
              </strong>
              <span v-else>
                {{countTypes.find(type => type.value == row.item.counter).text}}
              </span>
            </template>
            <template slot="total" slot-scope="row">
              {{formatNumber(row.item.total, row.item)}}
            </template>
            <template v-for="type in hcTypes" :slot="type.key" slot-scope="row">
              {{formatNumber(row.item[type.key], row.item)}}
            </template>
          </b-table>
        </b-col>
        <b-col cols=1 />
      </b-row>
    </section>

    <b-modal id="search-help" title="Which Search Option?" :hide-footer=true size=xl>
      Short answer: <strong>Problem Product</strong> is probably what you want<br/><br/>

      Long answer: These searches are looking for two different things on a Happy Customer order
      <ul>
        <li>
          <strong>Problem Product:</strong> This is looking for a special field in the note of an HC "Product"
          (Missing, ShipIssue, etc.) that lists the product on the order that had the issue.<br/><br/>

          For example, if a customer received a defective motor, the problem product would be the SKU of the motor.<br/><br/>

          If the customer received the same defective motor that was part of a KIT, the Problem Product would be
          the KIT, since it was the item on the order that had the issue, we'd just have <em>sent</em> the motor,
          not the entire KIT (see SO Item).
        </li>
        <li>
          <strong>SO Item:</strong> This is looking for an item that was <em>digitially</em> sent to a customer.
          This is notably different from being <em>physically</em> sent to a customer,
          because in most cases we're going to be physically sending something to the customer,
          but we only occasionally send items digitially on a HC.<br/><br/>

          Consider the following example: We've done an <strong>HC Missing</strong> order because
          a customer didn't receive something they ordered.<br/>
          In this case, we're going to be <em>physically</em> sending the customer an item
          (because they didn't get it - probably because we didn't ship it),
          but we won't be <em>digitally</em> sending them the item, because we took the item out
          of inventory on the original order, they just didn't get it.<br/><br/>

          This is different than the following example: We've done an <strong>HC ShipIssue</strong> order because
          the item was lost in shipping.<br/>
          This is going to result in us both sending the item <em>digitally</em> and <em>physically</em>, since the
          item that was taken out of inventory was lost in shipping, and we have to send an item that hasn't already
          been taken out of inventory to the customer now.
        </li>
      </ul>
    </b-modal>
    <b-modal id="unknown-help" title="Unknown Happy Customers?" :hide-footer=true>
      "Unknown" isn't a type of Happy Customer that has one specific reason.<br />
      This type comes a mistake in the filing of a Happy Customer that prevents us from linking the Happy Customer item with the item being sent.
      <br />
      There are several was this can happen. An (incomplete) list is below:
      <ul>
        <li>Misspelling the Problem Product</li>
        <li>Choosing to not ship the Problem Product, and misspelling the product to ship</li>
        <li>Choosing to not ship the Problem Product, and not listing the item to ship in replacement</li>
      </ul>
      Ideally, we'd never have any of these.<br/><br/>

      Alternatively, we also learned that these can be when kits or parts of kits are returned, a feature is coming soon™ to fix that issue.
    </b-modal>
  </b-container>
</template>

<script>
import moment from 'moment';
import axios from 'axios';
import utility from '../../../helpers/utility'
import NiceDate from '../../utility/NiceDate.vue';
const BASE_API = process.env.VUE_API_ROOT;

export default {
  components: {
    NiceDate
  },
  data() {
    return {
      endDate: moment().format('YYYY-MM-DD'),
      startDate: moment(`${new Date().getFullYear()}-01-01`).format('YYYY-MM-DD'),
      search: '',
      searchOption: 'problem-product',
      busy: true,

      hcTypes: [
        { key: 'Missing', label: 'Missing Item', sortable: true },
        { key: 'WrongPart', label: 'Wrong Part Sent (no return label)', sortable: true },
        { key: 'WrongPart_Return', label: 'Wrong Part Sent (with return label)', sortable: true },
        { key: 'Quality', label: 'Quality Issue (no return label)', sortable: true },
        { key: 'Quality_Return', label: 'Quality Issue (with return label)', sortable: true },
        { key: 'ShipIssue', label: 'Shipment Issue', sortable: true },
        { key: 'MiscIssue', label: 'Other Issue', sortable: true },
        { key: 'Unknown', label: 'Unknown Issue', sortable : true },
      ],
      countTypes: [
        { value: 'orders', text: 'Number of HC orders' },
        { value: 'mistakes', text: 'Count of unique HC mistakes' },
        { value: 'items', text: 'Sum of items with mistakes' },
        { value: 'price', text: 'Total price of the items SENT to customer + shipping' },
      ],
      searchOptions: [
        { text: 'Search By SO Item', value: 'order-item' },
        { text: 'Search by Problem Product', value: 'problem-product'}
      ],

      totals: { },
      search_totals: { },
      countFields: [ { key: 'orders', label: 'Orders (E/F/G/X/Y/?)' }, 'SKU Counts', 'itemQuantities' ],
      tables: {
        columns: [ 'problemProduct', 'count', 'sum', 'orders' ],
        maxLength: 5,
        quality: [],
        wrongPart: [],
        missing: []
      },

      sortBy: 'year',
      sortDesc: false,
      yearFields: [],
      yearData: [],
      monthName: '',
      monthFields: [],
      monthData: [],

      selectedCountType: 'mistakes',
    }
  },
  mounted() {
    this.yearFields = [
      { key: 'month', sortable: true },
      { key: 'year', sortable: true },
      { key: 'total', label: 'TOTAL', sortable: true, variant: 'secondary' }
    ].concat(this.hcTypes);
    this.monthFields = [
      { key: 'counter', sortable: true },
      { key: 'total', label: 'TOTAL', sortable: true, variant: 'secondary' }
    ].concat(this.hcTypes);
    this.getData();
  },
  computed: {
    monthToDateText() {
      let
        month = moment(this.endDate).format('MMMM'),
        year = moment(this.endDate).format('YYYY'),
        endDay = moment(this.endDate).format('DD')
      return `${month} 1-${endDay} ${year} (MTD)`
    },
  },
  methods: {
    clearData(e) {
      if (typeof(e) == "object") {
        e.preventDefault();
      }

      this.totals = { }
      this.search_totals = { }
      this.tables.quality = []
      this.tables.wrongPart = []
      this.tables.missing = []
      this.yearData = []
      this.monthName = ''
      this.monthData = []
    },
    formatDate(date) {
      return new Intl.DateTimeFormat("en-US", {dateStyle: 'long'}).format(new Date(`${date}T00:00:00`));
    },
    getData(e) {
      this.busy = true
      if (typeof(e) == "object") {
        e.preventDefault();
      }

      this.clearData();

      let end_date = moment();
      if (this.endDate != null) {
        end_date = moment(this.endDate);
      }
      let start_date = moment();
      if (this.startDate != null) {
        start_date = moment(this.startDate);
      }

      let params = {
          start_date: start_date.format('YYYY-MM-DD'),
          end_date: end_date.format('YYYY-MM-DD'),
          search: (typeof(this.search) === 'undefined' || this.search == '') ? '%' : this.search
        }

      axios.get(`${BASE_API}/fishbowl/reports/happy-customer/totals`, { params: params }).then((response) => {
        this.totals = response.data.totals

        let qualityDone = false, wrongPartDone = false, missingDone = false;
        this.tables.quality = []
        this.tables.wrongPart = []
        this.tables.missing = []
        response.data.num_totals.forEach(item => {
          if (qualityDone && wrongPartDone && missingDone) return
          switch (item.num) {
            case 'Quality':
              if (this.tables.quality.length >= this.tables.maxLength) {
                qualityDone = true
                break;
              }
              this.tables.quality.push(item)
              break;
            case 'WrongPart':
              if (this.tables.wrongPart.length >= this.tables.maxLength) {
                wrongPartDone = true
                break;
              }
              this.tables.wrongPart.push(item)
              break;
            case 'Missing':
              if (this.tables.missing.length >= this.tables.maxLength) {
                missingDone = true
                break;
              }
              this.tables.missing.push(item)
              break;
            default:
              break;
          }
        })
      })
      axios.get(`${BASE_API}/fishbowl/reports/happy-customer/search-totals`, { params: params }).then((response) => {
        this.search_totals = response.data.totals
      })

      axios.get(`${BASE_API}/fishbowl/reports/happy-customer/${this.searchOption}`, { params: params }).then((response) => {
        this.yearData = [];
        response.data.forEach((item) => {
          let m = this.yearData.find(x => x.month == item.month && x.year == item.year);
          if (m == null) {
            m = { month: item.month, year: item.year, total: { orders: 0, mistakes: 0, items: 0, price: 0 } };
            this.yearData.push(m)
          }
          m[item.type] = {
            orders: item.orders,
            mistakes: item.mistakes,
            items: item.items,
            price: item.price
          };
          m.total.orders += item.orders;
          m.total.mistakes += item.mistakes;
          m.total.items += item.items;
          m.total.price += item.price;
        });

        this.monthData = [];
        this.monthName = this.getMonthName(end_date.month()+1)
        let m = this.yearData.find(x => x.month == end_date.month()+1 && x.year == end_date.year())
        if (m == null) {
          for (let i = 0; i < this.countTypes.length; i++) {
            this.monthData.push({ counter: this.countTypes[i]['value'] });
          }
          return;
        }
        for (let key in m) {
          if (m.hasOwnProperty(key)) {
            for (let innerKey in m[key]) {
              if (m[key].hasOwnProperty(innerKey)) {
                let item = this.monthData.find(x => x.counter == innerKey);
                if (item == null) {
                  item = { counter: innerKey };
                  this.monthData.push(item);
                }
                item[key] = m[key][innerKey];
              }
            }
          }
        }
      }).catch((error) => {
        console.error(error);
      }).finally(() => {
        this.busy=false
      })
    },
    getOrdersHtml(nums) {
      return nums.split(',').map(o => `<a href="/sales-order/view/${o}" target='_blank'>${o}</a>`).join(", ")
    },
    getMonthName(number) {
      return moment(number, "M").format("MMMM")
    },
    formatNumber(ob, item) {
      let n = 0;
      if (ob == null ) {
        n = 0;
      } else if (typeof(ob) === 'object') {
        if (ob[this.selectedCountType] == null) {
          n = 0;
        } else {
          n = Number(ob[this.selectedCountType]);
        }
      } else {
        n = Number(ob);
      }

      n = Number(n);
      if (item.counter == "price" || (typeof(ob) === 'object' && this.selectedCountType == 'price')) {
        var formatter = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2
        });
        return formatter.format(n)
      } else {
        return n;
      }
    },
    betterRound(number, places = 2) {
      return utility.betterRound(number, places)
    },
  }
}
</script>

<style scoped>
hr {
  border-top-width: 3px;
  border-top-color: #bbb;
}
.custom-select {
  font-weight: 700;
  color: #212529
}
</style>



// WEBPACK FOOTER //
// src/components/sales-order/reports/HappyCustomerReport.vue